'use client'

/**
 * Balise de la mesure d'audience exemptée de consentement (08/10/2026).
 *
 * Montée dans le layout public HORS d'AnalyticsGate : elle ne dépend pas du
 * bandeau, parce qu'elle n'écrit ni ne lit aucun identifiant sur le terminal
 * — pas de cookie, pas de localStorage, pas de sessionStorage, pas
 * d'empreinte. Elle envoie seulement, pour chaque page vue : le chemin (sans
 * chaîne de requête), le DOMAINE du site d'où l'on vient (jamais l'adresse
 * complète) et `utm_source` s'il est dans l'adresse. Le reste (jour, type
 * d'appareil, visite) est calculé et agrégé côté serveur : voir
 * src/lib/audience/mesure.ts.
 *
 * Ne part JAMAIS si :
 *   - le visiteur a cliqué « Ne pas être compté » (cookie `sco2_audience_refus`) ;
 *   - le navigateur est piloté par un automate (`navigator.webdriver`).
 *
 * Ne part qu'après ENGAGEMENT : 5 s de page visible, ou une interaction
 * réelle (défilement, clic, toucher, touche). C'est ce qui écarte le robot de
 * /blog repéré le 14/09/2026, qui exécutait le JavaScript avec des
 * user-agents Chrome ordinaires, mais ne restait jamais, ne défilait jamais et
 * ne voyait jamais une deuxième page (mesuré sur 4 091 sessions). Une fois le
 * visiteur engagé, les pages suivantes de la navigation sont comptées tout de
 * suite.
 */
import { useEffect, useRef } from 'react'
import { usePathname } from 'next/navigation'
import { COOKIE_REFUS, SEUIL_VISIBLE_MS } from '@/lib/audience/mesure'

function refuse(): boolean {
  try {
    return document.cookie.split(';').some((c) => c.trim() === `${COOKIE_REFUS}=1`)
  } catch {
    return false
  }
}

function domaineVenu(): string {
  try {
    if (!document.referrer) return ''
    const h = new URL(document.referrer).hostname
    return h && h !== window.location.hostname ? h : ''
  } catch {
    return ''
  }
}

function envoyer(p: string, r: string, u: string) {
  if (refuse()) return
  try {
    void fetch('/api/audience', {
      method: 'POST',
      keepalive: true,
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ p, r, u }),
    }).catch(() => {})
  } catch {
    /* rien : la mesure ne doit jamais gêner la page */
  }
}

export default function AudienceBalise() {
  const pathname = usePathname()
  const engage = useRef(false)
  const enAttente = useRef<{ p: string; r: string; u: string }[]>([])
  const premiere = useRef(true)

  // Engagement : une fois pour toute la navigation.
  useEffect(() => {
    if (typeof navigator !== 'undefined' && navigator.webdriver) return
    if (refuse()) return

    let visible = 0
    let dernier = Date.now()
    const vider = () => {
      if (engage.current) return
      engage.current = true
      for (const e of enAttente.current) envoyer(e.p, e.r, e.u)
      enAttente.current = []
      nettoyer()
    }
    const minuterie = window.setInterval(() => {
      const t = Date.now()
      if (document.visibilityState === 'visible') visible += t - dernier
      dernier = t
      if (visible >= SEUIL_VISIBLE_MS) vider()
    }, 250)
    const evenements = ['scroll', 'pointerdown', 'keydown', 'touchstart'] as const
    const opts: AddEventListenerOptions = { passive: true, capture: true }
    for (const ev of evenements) window.addEventListener(ev, vider, opts)
    function nettoyer() {
      window.clearInterval(minuterie)
      for (const ev of evenements) window.removeEventListener(ev, vider, opts)
    }
    return nettoyer
  }, [])

  // Une page vue par chemin affiché (premier chargement et navigations internes).
  useEffect(() => {
    if (typeof navigator !== 'undefined' && navigator.webdriver) return
    if (refuse()) return
    const p = window.location.pathname
    let r = ''
    let u = ''
    if (premiere.current) {
      premiere.current = false
      r = domaineVenu()
      try { u = new URLSearchParams(window.location.search).get('utm_source') || '' } catch { u = '' }
    }
    if (engage.current) envoyer(p, r, u)
    else enAttente.current.push({ p, r, u })
  }, [pathname])

  return null
}
